import classes from "../../assets/scss/modules/PublicListingSingle.module.scss";
import React, {Fragment, useState} from 'react';
import {useSingleListing} from "../context/SingleListingProvider.tsx";
import useCtrValeursFixes from "../../hooks/useCtrValeursFixes.ts";
import {CtrDomainsVf, Query, QueryMcgillProjectArgs} from "../../graphql/generated/graphql-main.tsx";
import useCtrMetaFields from "../../hooks/useCtrMetaFields.tsx";
import useCtrGenresProprietes from "../../hooks/useCtrGenresProprietes.ts";
import {Chip, CircularProgress, IconButton, Link, Skeleton} from "@mui/material";
import useCtrRegions from "../../hooks/useCtrRegions.ts";
import {FileDownload, Place} from "@mui/icons-material";
import clsx from "clsx";
import useCtrInscription from "../../hooks/useCtrInscription.tsx";
import {useQuery} from "@apollo/client/react";
import {gqlTemplates} from "../../utils/gql-client.ts";
import Button from "@mui/material/Button";
import useReactTexts from "../../hooks/useReactTexts.ts";
import {cfg} from "../../config.ts";
import axios, {AxiosResponse} from "axios";
import {apiUrls, str2key} from "../../utils/utils.ts";
import {useWpContext} from "../context/WordPressProvider.tsx";

const cssroot = "xlh";

type Props = {}
const ListingHeader = (props: Props) => {
    const {others, is_fr} = useWpContext<ZListingSingle>();
    const {listing: lst} = useSingleListing();
    const {loading: loadingMeta} = useCtrMetaFields();
    const {fixedValue, loading: loadingFixed} = useCtrValeursFixes();
    const {loading: loadingGenres} = useCtrGenresProprietes();
    const {loading: loadingRegions} = useCtrRegions();
    const {titleWithWaterBody, price, displayAddress} = useCtrInscription();
    const {__} = useReactTexts();

    const [printing, setPrinting] = useState<boolean>(false);

    // query for McGill project potentially associated with this listing
    const {data} = useQuery<Query, QueryMcgillProjectArgs>(gqlTemplates.mcgillProject,
        {variables: {mls: lst?.NO_INSCRIPTION!}});

    const propertyTitle = titleWithWaterBody(lst);
    const seoTitle = `${propertyTitle} - ${displayAddress(lst)}`;
    const mcgillProjectUrl = data?.mcgillProject ?? null;

    /**
     * Trigger process of capturing current listing URL, sending it to hub for PDF printing via Puppeteer,
     * and returning final PDF to user for in-browser download.
     */
    const onPrintListing = async () => {
        setPrinting(true);

        // generate printable url that loads print-specific React modules for current listing (see index.tsx)
        const currentUrl = new URL(window.location.href);
        const printUrl = `${currentUrl.origin}/print-listing/${others.lang}/${others.mls_id}`;
        const url2print = encodeURIComponent(printUrl);

        // execute query
        const {root_core, core_print_listing} = cfg.api.rest_paths;
        const endpointUrl = `${apiUrls.rest()}/${root_core}/${core_print_listing}/${url2print}`;
        const response: AxiosResponse<Blob, any> = await axios.get(endpointUrl, {responseType: "blob"});

        // prepare Blob for download in browser & open in new window
        const blob = new Blob([response.data], {type: response.headers['content-type']});
        const blobUrl = URL.createObjectURL(blob);

        // create temporary link to set proper download file name & download directly instead of displaying
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `EV-${str2key(displayAddress(lst))}.pdf`;
        document.body.appendChild(link);
        setPrinting(false);
        link.click();
        document.body.removeChild(link);
    }

    // skeleton loading
    if (loadingMeta || loadingFixed || loadingGenres || loadingRegions) {
        return <Fragment>
            <div className={"flex justify-between gap-x-4"}>
                <Skeleton variant={"rectangular"} height={75} width={"100%"}/>
                <Skeleton variant={"rectangular"} height={75} width={"100%"}/>
            </div>
            <div className={"flex justify-between gap-x-4 mt-4"}>
                <Skeleton variant={"rectangular"} height={75} width={"100%"}/>
                <Skeleton variant={"rectangular"} height={75} width={"100%"}/>
            </div>
        </Fragment>
    }

    return <Fragment>

        {/* metadata (handled automatically by React 19) */}
        <title>{seoTitle}</title>
        <meta property={"og:title"} content={seoTitle}/>
        <meta name={"twitter:title"} content={seoTitle}/>

        {/* main header - type of property & price */}
        <h1 className={clsx(`${cssroot}__header1 flex flex-col md:flex-row justify-between mb-0`,
            classes.title_price)}>
            {/* display genre of property or category if no 'genre' defined */}
            <div className={`${cssroot}__title`}>{propertyTitle}</div>

            {/* price & print button icon */}
            <div className={`${cssroot}__price items-center flex flex-row`}>
                <div className={"flex flex-col items-start leading-tight"}>
                    <div>{price(lst)}</div>
                    {lst?.CODE_STATUT == "VE" && (
                        <span className={"text-xs font-normal leading-none opacity-70"}>
                            {__("Prix demandé")}
                        </span>
                    )}
                </div>
                <IconButton
                    className={"bg-carbon text-white hover:bg-merino hover:text-carbon" +
                        " active:bg-merino active:text-carbon focus:bg-carbon focus:text-white " +
                        "  rounded-none px-4"}
                    onClick={onPrintListing}
                >
                    {printing
                        ? <CircularProgress size={24} className={"text-white"}/>
                        : <FileDownload/>}
                </IconButton>
            </div>
        </h1>

        {/* address & MLS ID */}
        <h2 className={clsx(`${cssroot}__header2 flex flex-wrap flex-col justify-between mb-0 lg:flex-row lg:items-center`,
            classes.address_mls)}>

            {/* address + GPS button icon */}
            <div className={`${cssroot}__address flex items-center space-x-4`}>

                {/* text address */}
                <div className={"address"}>
                    {/* display full address, including street, municipality, region, etc. */}
                    <span>{displayAddress(lst)}</span>

                    {/* note: the Centris GPS coordinates are not super accurate (string address is more) */}
                    {/* https://stackoverflow.com/questions/30544268/create-google-maps-links-based-on-coordinates */}
                    <IconButton
                        color={"primary"} component={Link} target={"_blank"}
                        href={`https://www.google.com/maps/place/${lst?.LATITUDE},${lst?.LONGITUDE}`}
                    >
                        <Place/>
                    </IconButton>
                </div>

                {/* listing's status -- show ONLY if 'sold' (VE) */}
                {lst?.CODE_STATUT == "VE" && <Chip
                    color={"error"}
                    className={"font-semibold"}
                    label={fixedValue(CtrDomainsVf.CodeStatut, lst?.CODE_STATUT)}
                ></Chip>}
            </div>

            {/* McGill project if applicable */}
            <div className={`${cssroot}__project flex items-center`}>
                {mcgillProjectUrl && <Button
                    className={"normal-case rounded-none text-white"}
                    variant={"contained"}
                    color={"error"}
                    href={`https://${mcgillProjectUrl}`}
                    target={"_blank"}
                >
                    {__("Voir le projet")}
                </Button>}
            </div>
        </h2>
    </Fragment>
};

export default ListingHeader;
